/**
 * Frontend permission helpers — mirrors backend ranking in
 * ``app.models.role_satisfies``. UI uses these to decide whether to
 * render mutation affordances; the backend enforces authoritatively, so
 * a UI bug here cannot grant access — only hide buttons that wouldn't
 * work anyway.
 */
import type { WorkspaceRole } from '@/api/queries'

const ROLE_RANK: Record<WorkspaceRole, number> = {
  viewer: 1,
  editor: 2,
  admin: 3,
  owner: 4,
}

/** True iff ``actual`` rank ≥ ``required`` rank. */
export function roleSatisfies(
  actual: WorkspaceRole,
  required: WorkspaceRole,
): boolean {
  return ROLE_RANK[actual] >= ROLE_RANK[required]
}

/** Whether the caller can invite, revoke, change roles, or remove others. */
export function canManageMembers(role: WorkspaceRole): boolean {
  return roleSatisfies(role, 'admin')
}

/** Whether the caller can read the workspace audit log. */
export function canViewAudit(role: WorkspaceRole): boolean {
  return roleSatisfies(role, 'admin')
}

/** Whether the caller can grant or revoke shares on a document. */
export function canManageShares(role: WorkspaceRole): boolean {
  return roleSatisfies(role, 'admin')
}

/**
 * Role transitions the caller is allowed to *offer* in the UI.
 *
 * Mirrors the backend's `update_member_role` rules:
 *   - Only owners can promote a member to owner.
 *   - The "last owner" demotion check is a server invariant — the UI
 *     keeps the option visible but disables it via {@link blockedDemotion}
 *     when there's only one owner left.
 */
export function assignableRoles(
  callerRole: WorkspaceRole,
): readonly WorkspaceRole[] {
  if (callerRole === 'owner') {
    return ['owner', 'admin', 'editor', 'viewer'] as const
  }
  if (callerRole === 'admin') {
    return ['admin', 'editor', 'viewer'] as const
  }
  return [] as const
}

/**
 * Returns a denial reason string when ``target`` cannot be demoted from
 * owner because they're the last one standing. ``null`` means the
 * transition is allowed.
 */
export function blockedDemotion(args: {
  targetRole: WorkspaceRole
  newRole: WorkspaceRole
  ownerCount: number
}): string | null {
  const { targetRole, newRole, ownerCount } = args
  if (targetRole === 'owner' && newRole !== 'owner' && ownerCount <= 1) {
    return 'Cannot demote the last owner'
  }
  return null
}
